/**
 * Kullanıcı Yönetimi — eski `newuser.php` karşılığı.
 *
 * Eski ekranın düzeltilen hataları:
 *  · Şifre formda düz metin alınıp sunucuda saklanıyordu. Burada ŞİFRE ALANI
 *    YOKTUR — kullanıcıya davet e-postası gider, ilk şifreyi kendisi belirler
 *    (Identity Platform şifre politikası altında).
 *  · `usrID == 2` süper kullanıcı hilesi kaldırıldı; yetki yalnızca claim'dedir.
 *  · Ekranda yalnızca "Mevcut Kullanıcılar" listesi vardı; pasife alma yalnızca
 *    veritabanından yapılabiliyordu (`outofwork`). Artık ekrandan yapılır.
 *  · Son iş sahibi pasife alınabiliyordu → sistem yönetilemez hale gelirdi.
 *    Buton engellenir; sunucu ayrıca reddeder.
 *
 * Yetki: YALNIZCA `boss`. Tüm mutasyonlar callable üzerindendir; `users`
 * koleksiyonuna istemciden yazma yolu HİÇ yoktur (yetki yükseltme yüzeyi sıfır).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { orderBy } from "firebase/firestore";
import { PageHeader, Modal, Spinner, EmptyState, StatusBadge, useToast } from "../components";
import { useCollection, useCallableAction } from "../hooks";
import { useCan, useSession, type UserRole } from "../lib/auth";
import { dt, n } from "../lib/format";
import { NoAccess } from "../components";
import { ErrorNote, Field } from "../components";

export interface ErpUser {
  /** Silinmiş kayıt — denetim izi için durur, listede gösterilmez. */
  deleted?: boolean;
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  active: boolean;
  costView?: boolean;
  financeView?: boolean;
  lastLoginAt?: { toDate: () => Date } | null;
  createdAt?: { toDate: () => Date } | null;
  /** Davet gönderildi ama kullanıcı henüz şifresini belirlemedi. */
  pendingInvite?: boolean;
  inviteId?: string | null;
  /**
   * `users/{uid}` var ama Firebase Auth hesabı YOK. İstemci bunu ölçemez
   * (Admin SDK gerekir); `checkUserAccounts` sunucuda ölçüp buraya damgalar.
   */
  authMissing?: boolean;
  /** Öksüz kayıt temizlendi — bir daha "sorunlu kayıt" olarak listelenmez. */
  orphanCleanedAt?: { toDate: () => Date } | null;
}

/** Firestore Timestamp benzeri değerler için ortak biçim. */
type Ts = { toDate: () => Date; toMillis?: () => number } | null | undefined;

export interface InviteDoc {
  id: string;
  email: string;
  /**
   * ★ SUNUCU BU ALANI YAZMIYORDU — ROL SÜTUNU BU YÜZDEN BOŞTU.
   * Ölçüm: canlıdaki 6 davetin 6'sında da `role` alanı YOKTU; ekran
   * `ROLE_TR[invite.role]` okuyunca `undefined` alıyor ve hücre boş kalıyordu.
   * Firestore olmayan alan için hata vermez. Sunucu artık yazıyor
   * (`writeInviteDoc`), eski kayıtlar için aşağıda kullanıcı kaydından
   * yedek okuma yapılıyor.
   */
  role?: UserRole;
  status?: "pending" | "accepted" | "expired" | "revoked";
  /** Sunucu bir dönem yalnızca `createdAt` yazdı; ikisi de okunur. */
  sentAt?: Ts;
  createdAt?: Ts;
  expiresAt?: Ts;
}

/** Davetin ekranda gösterilen durumu. Sunucudaki `inviteStateOf` ile aynı kural. */
type InviteState = "pending" | "expired" | "revoked" | "accepted";

function msOf(ts: Ts): number | null {
  if (!ts) return null;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  try {
    return ts.toDate().getTime();
  } catch {
    return null;
  }
}

/**
 * ★ "BEKLİYOR" İLE "SÜRESİ DOLDU" AYNI ŞEY DEĞİL.
 *
 * `status` alanı zamanla kendiliğinden değişmez; süre dolması bir olay değil bir
 * KARŞILAŞTIRMADIR ve okuma anında yapılmalıdır. Ekran yalnızca `status`'e
 * baktığı için canlıdaki 6 ölü davetin hepsi geçerli davet gibi görünüyordu.
 * `expiresAt` hiç yoksa "geçerli" varsaymıyoruz — o alan yazılmadan önceki eski
 * kayıtlardır, süresi dolmuş sayılır.
 */
function inviteStateOf(i: InviteDoc, nowMs: number): InviteState {
  const status = i.status ?? "pending";
  if (status === "revoked" || status === "accepted") return status;
  const exp = msOf(i.expiresAt);
  if (exp == null) return "expired";
  return exp > nowMs ? "pending" : "expired";
}

/** Ekranda gösterilen davet satırı — ham dokümana türetilmiş alanlar eklenir. */
interface InviteRow extends InviteDoc {
  state: InviteState;
  effectiveRole?: UserRole;
  sortMs: number;
}

/** `checkUserAccounts` çıktısı: Auth hesabı olmayan `users` dokümanı. */
interface OrphanRow {
  uid: string;
  email: string;
  fullName: string;
  role: string;
  active: boolean;
  deleted: boolean;
}

/** Eski `newuser.php:124` etiketleriyle birebir aynı. */
export const ROLE_TR: Record<UserRole, string> = {
  production: "Üretim",
  office: "Ofis",
  boss: "İş Sahibi",
};

const ROLE_HINT: Record<UserRole, string> = {
  boss: "Maliyet, kâr, kullanıcı ve ayar yetkisi dâhil her şey.",
  office: "Sipariş, üretim, satın alma ve alış fiyatı. Ürün maliyeti ve kârı GÖRÜNMEZ.",
  production: "Üretim ve kesim listesi. Ürün/reçete/parça salt okunur; fiyat görünmez.",
};

const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

export default function Users() {
  const can = useCan();

  if (!can.isBoss) {
    return (
      <PageHeader title="Kullanıcı Yönetimi">
        <NoAccess what="Kullanıcı listesi ve rol yönetimi" />
      </PageHeader>
    );
  }

  return (
    <PageHeader title="Kullanıcı Yönetimi">
      <UserList />
    </PageHeader>
  );
}

function UserList() {
  const session = useSession();
  const toast = useToast();
  const { data: allUsers, loading, error } = useCollection<ErpUser>("users", [orderBy("fullName")]);

  /**
   * ★ SİLİNMİŞ KULLANICI LİSTEDE GÖRÜNMEZ.
   *
   * Kayıt denetim izi için Firestore'da KALIR (`deleted: true`) ama giriş hesabı
   * silinmiştir. Listede durduğu sürece yönetici ona "Aktifleştir" diyebiliyor,
   * sistem olmayan bir hesaba yetki yazmaya çalışıyor ve "yetkiler
   * uygulanamadı" hatası veriyordu — sebebi ekranda hiç görünmeden.
   */
  const users = allUsers.filter((u) => u.deleted !== true);

  /**
   * ★ SIRALAMA SUNUCUDA DEĞİL BURADA YAPILIR — VE BUNUN SEBEBİ ÖNEMLİ.
   *
   * Burada `orderBy("sentAt","desc")` vardı. Firestore'da SIRALAMA ALANI OLMAYAN
   * DOKÜMAN SONUÇ KÜMESİNDEN TAMAMEN DÜŞER ve hata da vermez. Sunucu ise
   * `sentAt` alanını hiç yazmıyordu (yalnızca `createdAt`). Canlıda ölçüldü:
   * 6 bekleyen davetin yalnızca 1'inde `sentAt` vardı → ekranda "Bekleyen
   * Davetler (1)" yazıyordu, 5 davet GÖRÜNMÜYORDU bile. Görünmeyen davet
   * iptal de edilemez, yenilenemez de.
   *
   * Sunucu artık `sentAt` yazıyor; ama ESKİ kayıtlar bir daha kaybolmasın diye
   * sıralama istemciye alındı ve `sentAt ?? createdAt` üzerinden yapılıyor.
   * Bu liste en fazla birkaç düzine satırdır; istemcide sıralamanın maliyeti yok.
   */
  const { data: invites } = useCollection<InviteDoc>("invites");

  /**
   * ★ ÇAĞRI ADLARI SUNUCUDAKİ EXPORT ADIYLA BİREBİR OLMAK ZORUNDA.
   *
   * Burada `setUserRole` / `setUserActive` / `createUser` yazıyordu; sunucuda
   * bu adlarda fonksiyon YOK (`updateErpUser`, `createErpUser` var). Olmayan
   * bir callable çağrılınca Firebase `internal` döner — yani ekranda
   * "İşlem başarısız (internal)" görünür ve sunucu loglarında hiçbir iz kalmaz.
   * Kullanıcı yönetimi ekranının TAMAMI bu yüzden çalışmıyordu.
   */
  const setRole = useCallableAction<{ uid: string; role: UserRole }, { ok: boolean }>(
    "updateErpUser",
  );
  const setActive = useCallableAction<{ uid: string; active: boolean }, { ok: boolean }>(
    "updateErpUser",
  );
  const resend = useCallableAction<{ uid: string }, { inviteLink: string; inviteExpiresAt: number }>(
    "resendInvite",
  );
  /**
   * ★ SİLME. Devre dışı bırakmak e-postayı serbest BIRAKMAZ; yarım kalmış bir
   * davetten sonra aynı adrese yeniden davet göndermek isteyen yönetici
   * "Bu e-posta ile kayıtlı bir kullanıcı zaten var." duvarına çarpıyor ve
   * çıkış yolu bulamıyordu.
   */
  const remove = useCallableAction<{ uid: string }, { email: string; message: string }>(
    "deleteErpUser",
  );
  /**
   * ★ DAVETİ İPTAL ET. Bekleyen davetler tablosunda hiçbir satırın iptal yolu
   * YOKTU: süresi dolmuş davet geçerli davetle aynı görünüyor ve listeden
   * kaldırılamıyordu. Yalnızca DAVET iptal edilir; kullanıcı kaydı ve giriş
   * hesabı yerinde kalır (onay penceresi bunu açıkça yazar).
   */
  const revoke = useCallableAction<
    { uid: string },
    { email: string; message: string; linkStillLive: boolean }
  >("revokeInvite");
  /**
   * ★ ÖKSÜZ KAYIT TARAMASI. `users/{uid}` var ama Firebase Auth hesabı yok olan
   * kayıtlar ekranda hiçbir şekilde görünmüyordu — istemci Auth'a soramaz.
   * Sunucu tek çağrıda tarar; kayıt `deleted:true` ise listeden zaten düşmüş
   * ama arkada kapanmayan bir `user-claims-desync` uyarısı üretmeye devam eder.
   */
  const scanAccounts = useCallableAction<
    Record<string, never>,
    { orphans: OrphanRow[]; checked: number; acceptedInvites: number }
  >("checkUserAccounts");
  const cleanOrphan = useCallableAction<
    { uid: string },
    { email: string; message: string; resolvedAlerts: number }
  >("cleanupOrphanUser");

  const [inviting, setInviting] = useState(false);
  const [confirm, setConfirm] = useState<{ user: ErpUser; next: boolean } | null>(null);
  /** Yenilenen davetin bağlantısı — e-posta gönderilmediği için ekranda gösterilir. */
  const [resent, setResent] = useState<{ link: string; expiresAt: number; email: string } | null>(null);
  /** Silme onayı bekleyen kullanıcı. */
  const [deleting, setDeleting] = useState<ErpUser | null>(null);
  /** İptal onayı bekleyen davet. */
  const [revoking, setRevoking] = useState<InviteRow | null>(null);
  /** Temizleme onayı bekleyen öksüz kayıt. */
  const [cleaning, setCleaning] = useState<OrphanRow | null>(null);
  const [orphans, setOrphans] = useState<OrphanRow[]>([]);

  /**
   * Öksüz taraması ekran açılışında BİR KEZ çalışır. Auth sorgusu tek bir toplu
   * çağrıdır (`getUsers`), maliyeti ihmal edilebilir; ama her render'da
   * tekrarlanmasın diye bayrakla korunur.
   */
  const scannedRef = useRef(false);
  useEffect(() => {
    if (scannedRef.current) return;
    scannedRef.current = true;
    void scanAccounts.run({}).then((res) => {
      if (res) setOrphans(res.orphans);
    });
  }, [scanAccounts]);

  /** Son aktif iş sahibi devre dışı bırakılamaz — sistem yönetilemez kalırdı. */
  const activeBossCount = useMemo(
    () => users.filter((u) => u.role === "boss" && u.active).length,
    [users],
  );

  /**
   * Görünen davet listesi. Üç şey burada birleşir:
   *  · durum okuma anında TÜRETİLİR (süresi dolan davet kendini "pending" sanar),
   *  · rol alanı yoksa KULLANICI KAYDINDAN tamamlanır (eski davetlerde `role` yok),
   *  · sıralama `sentAt ?? createdAt` ile istemcide yapılır (alanı olmayan
   *    doküman sunucu sıralamasından düşüyordu).
   * İptal edilmiş ve kabul edilmiş davetler listelenmez.
   */
  const inviteRows: InviteRow[] = useMemo(() => {
    const now = Date.now();
    const roleByUid = new Map(allUsers.map((u) => [u.id, u.role]));
    return invites
      .map((i) => ({
        ...i,
        state: inviteStateOf(i, now),
        // Davet dokümanındaki rol yoksa kullanıcı kaydındaki rol gösterilir;
        // ikisi de yoksa "—" yazılır. Boş hücre bırakmak, veriyi kaybetmiş
        // gibi görünmenin en sessiz yoludur.
        effectiveRole: i.role ?? roleByUid.get(i.id),
        sortMs: msOf(i.sentAt) ?? msOf(i.createdAt) ?? 0,
      }))
      .filter((i) => i.state === "pending" || i.state === "expired")
      .sort((a, b) => b.sortMs - a.sortMs);
  }, [invites, allUsers]);

  const expiredCount = inviteRows.filter((i) => i.state === "expired").length;

  /**
   * ★ `users.pendingInvite` ALANI HİÇ YAZILMIYOR — ölçüldü: 9 kullanıcı
   * dokümanının hiçbirinde yok. Ekran ise satırdaki “davet bekliyor” rozetini ve
   * “Daveti Yenile” düğmesini bu alana bağlamıştı; ikisi de HİÇ GÖRÜNMÜYORDU.
   * Doğrusu davet koleksiyonundan türetmektir: davet kaydı zaten uid ile
   * eşleşiyor, ikinci bir alanı senkron tutmaya gerek yok (ve senkron tutulmayan
   * ikinci alan er geç yanlış olur).
   */
  const inviteStateByUid = useMemo(
    () => new Map(inviteRows.map((i) => [i.id, i.state])),
    [inviteRows],
  );

  function isLastBoss(u: ErpUser) {
    return u.role === "boss" && u.active && activeBossCount <= 1;
  }

  async function applyRole(u: ErpUser, role: UserRole) {
    const res = await setRole.run({ uid: u.id, role });
    if (res) toast.success(`${u.fullName}: rol ${ROLE_TR[role]} olarak güncellendi.`);
  }

  async function applyActive(u: ErpUser, active: boolean) {
    const res = await setActive.run({ uid: u.id, active });
    if (res) {
      toast.success(`${u.fullName} ${active ? "aktifleştirildi" : "devre dışı bırakıldı"}.`);
      setConfirm(null);
    }
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-slate-500">
          Şifre bu ekranda belirlenmez. Yeni kullanıcıya <strong>davet bağlantısı</strong> gönderilir;
          şifresini kendisi oluşturur. Rol değişikliği token'ı tazeler ve yetki anında güncellenir.
        </p>
        <button
          type="button"
          onClick={() => setInviting(true)}
          className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
        >
          Kullanıcı Davet Et
        </button>
      </div>

      <ErrorNote error={error ?? setRole.error ?? setActive.error ?? resend.error} />
      {loading ? <Spinner /> : null}

      {!loading && users.length === 0 ? (
        <EmptyState title="Kullanıcı yok" description="İlk kullanıcıyı davet ederek başlayın." />
      ) : null}

      {users.length > 0 ? (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                {/* ★ TABLET/TELEFON: 7 sütun 753px yer istiyordu, tablet dikeyde
                    (720px kap) taşıyordu. İkincil sütunlar dar ekranda gizlenir;
                    e-posta ve son giriş "Ad Soyad" hücresinin altında görünür. */}
                <th className="px-3 py-2">Ad Soyad</th>
                <th className="hidden px-3 py-2 lg:table-cell">E-posta</th>
                <th className="w-44 px-3 py-2">Rol</th>
                <th className="hidden px-3 py-2 lg:table-cell">Maliyet / Kâr</th>
                <th className="px-3 py-2">Durum</th>
                <th className="hidden px-3 py-2 lg:table-cell">Son Giriş</th>
                <th className="px-3 py-2 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => {
                const isSelf = session?.user.uid === u.id;
                const inviteState = inviteStateByUid.get(u.id);
                return (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <span className="font-medium text-slate-800">{u.fullName || "—"}</span>
                      {isSelf ? (
                        <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                          siz
                        </span>
                      ) : null}
                      {/* Dar ekranda gizlenen sütunların içeriği burada görünür. */}
                      <span className="mt-1 block break-all text-xs text-slate-600 lg:hidden">
                        {u.email}
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-600 lg:hidden">
                        Son giriş: {dt(u.lastLoginAt ?? null)}
                      </span>
                    </td>
                    <td className="hidden px-3 py-2 text-slate-600 lg:table-cell">{u.email}</td>
                    <td className="px-3 py-2">
                      <select
                        className={INPUT}
                        value={u.role}
                        /* Kendi rolünü değiştiremez — kilitlenmeyi önler. */
                        disabled={isSelf || setRole.loading}
                        onChange={(e) => void applyRole(u, e.target.value as UserRole)}
                      >
                        {(Object.keys(ROLE_TR) as UserRole[]).map((r) => (
                          <option key={r} value={r}>
                            {ROLE_TR[r]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="hidden px-3 py-2 text-xs text-slate-600 lg:table-cell">
                      {u.financeView ? (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800">
                          Maliyet + Kâr
                        </span>
                      ) : u.costView ? (
                        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-sky-800">
                          Alış fiyatı
                        </span>
                      ) : (
                        <span className="text-slate-600">yok</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge
                        label={u.active ? "Aktif" : "Devre dışı"}
                        color={u.active ? "success" : "neutral"}
                      />
                      {inviteState === "pending" ? (
                        <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                          davet bekliyor
                        </span>
                      ) : inviteState === "expired" ? (
                        <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800">
                          daveti süresi doldu
                        </span>
                      ) : null}
                      {u.authMissing ? (
                        <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                          Auth hesabı yok
                        </span>
                      ) : null}
                    </td>
                    <td className="hidden px-3 py-2 text-slate-500 lg:table-cell">
                      {dt(u.lastLoginAt ?? null)}
                    </td>
                    {/* ★ Eylemler dar ekranda alt alta dizilir ve 44px dokunma
                        hedefi alır; masaüstünde eski yan yana sıra korunur. */}
                    <td className="px-3 py-2 text-right lg:whitespace-nowrap">
                      <div className="flex flex-col items-stretch gap-1 lg:flex-row lg:items-center lg:justify-end lg:gap-0">
                      {inviteState ? (
                        <button
                          type="button"
                          disabled={resend.loading}
                          onClick={async () => {
                            const res = await resend.run({ uid: u.id });
                            if (res) setResent({ link: res.inviteLink, expiresAt: res.inviteExpiresAt, email: u.email ?? "" });
                          }}
                          className="inline-flex min-h-[44px] items-center justify-center rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100 lg:min-h-0 lg:px-2"
                        >
                          Daveti Yenile
                        </button>
                      ) : null}
                      <button
                        type="button"
                        disabled={isSelf || isLastBoss(u) || setActive.loading}
                        title={
                          isSelf
                            ? "Kendi hesabınızı devre dışı bırakamazsınız."
                            : isLastBoss(u)
                              ? "Son aktif iş sahibi devre dışı bırakılamaz."
                              : undefined
                        }
                        onClick={() => setConfirm({ user: u, next: !u.active })}
                        className={
                          "inline-flex min-h-[44px] items-center justify-center rounded border px-3 py-1 text-xs disabled:opacity-40 lg:ml-1 lg:min-h-0 lg:px-2 " +
                          (u.active
                            ? "border-red-300 text-red-700 hover:bg-red-50"
                            : "border-emerald-300 text-emerald-700 hover:bg-emerald-50")
                        }
                      >
                        {u.active ? "Devre Dışı Bırak" : "Aktifleştir"}
                      </button>
                      {/* ★ SİL — e-postayı serbest bırakır, yeniden davet
                          edilebilir hâle getirir. Firestore kaydı silinmez,
                          `deleted` işaretlenir: denetim kaydının "kim yaptı"
                          bağlantısı kopmamalı. */}
                      {!isSelf ? (
                        <button
                          type="button"
                          disabled={remove.loading}
                          onClick={() => setDeleting(u)}
                          className="inline-flex min-h-[44px] items-center justify-center rounded border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-40 lg:ml-1 lg:min-h-0 lg:px-2"
                        >
                          Sil
                        </button>
                      ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {activeBossCount <= 1 ? (
        <p className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Sistemde tek aktif iş sahibi var. Devre dışı bırakma engellenmiştir; yedek bir iş sahibi
          tanımlamanız önerilir.
        </p>
      ) : null}

      {/* ── Öksüz kayıtlar ───────────────────────────────────────────────── */}
      {orphans.length > 0 ? (
        <section className="mt-8">
          <h3 className="mb-1 text-base font-semibold text-slate-800">
            Sorunlu Kayıtlar ({n(orphans.length)})
          </h3>
          <p className="mb-2 max-w-3xl text-xs text-slate-600">
            Bu kayıtların <strong>Firebase Auth hesabı yok</strong> — kullanıcı dokümanı duruyor
            ama giriş hesabı silinmiş. Böyle bir kayda yetki yazılamaz; sistem her denemede
            “Kullanıcının yetkisi ile kaydı birbirini tutmuyor” uyarısı üretir ve
            <strong> “işlemi tekrar çalıştırın” tavsiyesi asla düzeltmez</strong>. Kaydı
            temizlemek uyarıyı da kapatır.
          </p>
          <div className="overflow-x-auto rounded border border-amber-300">
            <table className="min-w-full text-sm">
              <thead className="bg-amber-50 text-left text-xs uppercase tracking-wide text-amber-900">
                <tr>
                  <th className="px-3 py-2">Ad Soyad / E-posta</th>
                  <th className="px-3 py-2">Durum</th>
                  <th className="px-3 py-2 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {orphans.map((o) => (
                  <tr key={o.uid}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-slate-800">{o.fullName || "—"}</span>
                      <span className="mt-0.5 block break-all text-xs text-slate-600">
                        {o.email || o.uid}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                        Auth hesabı yok — öksüz kayıt
                      </span>
                      {o.deleted ? (
                        <span className="ml-1 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">
                          silinmiş
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled={cleanOrphan.loading}
                        onClick={() => setCleaning(o)}
                        className="inline-flex min-h-[44px] items-center justify-center rounded border border-amber-400 px-3 py-1 text-xs text-amber-900 hover:bg-amber-50 disabled:opacity-40 lg:min-h-0 lg:px-2"
                      >
                        Kaydı Temizle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ErrorNote error={scanAccounts.error ?? cleanOrphan.error} />
        </section>
      ) : null}

      {/* ── Bekleyen davetler ────────────────────────────────────────────── */}
      {inviteRows.length > 0 ? (
        <section className="mt-8">
          <h3 className="mb-1 text-base font-semibold text-slate-800">
            Bekleyen Davetler ({n(inviteRows.length)})
            {expiredCount > 0 ? (
              <span className="ml-2 rounded bg-red-100 px-2 py-0.5 text-xs font-normal text-red-800">
                {n(expiredCount)} tanesinin süresi doldu
              </span>
            ) : null}
          </h3>
          <p className="mb-2 max-w-3xl text-xs text-slate-600">
            Süresi dolmuş davetin bağlantısı artık çalışmaz; kişi şifresini belirleyemez.
            <strong> Yeniden Gönder</strong> yeni bir bağlantı üretir,{" "}
            <strong>Daveti İptal Et</strong> ise yalnızca daveti kapatır — kullanıcı kaydı
            ve giriş hesabı yerinde kalır.
          </p>
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2">E-posta</th>
                  <th className="px-3 py-2">Rol</th>
                  <th className="px-3 py-2">Durum</th>
                  <th className="hidden px-3 py-2 lg:table-cell">Gönderildi</th>
                  <th className="hidden px-3 py-2 lg:table-cell">Geçerlilik</th>
                  <th className="px-3 py-2 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {inviteRows.map((i) => (
                  <tr key={i.id} className={i.state === "expired" ? "bg-red-50/40" : undefined}>
                    <td className="px-3 py-2 text-slate-700">
                      <span className="break-all">{i.email}</span>
                      <span className="mt-0.5 block text-xs text-slate-500 lg:hidden">
                        Gönderildi: {dt(i.sentAt ?? i.createdAt ?? null)}
                      </span>
                      <span className="block text-xs text-slate-500 lg:hidden">
                        Geçerlilik: {dt(i.expiresAt ?? null)}
                      </span>
                    </td>
                    {/* Rol davet dokümanında yoksa kullanıcı kaydından gelir. */}
                    <td className="px-3 py-2 text-slate-600">
                      {i.effectiveRole ? ROLE_TR[i.effectiveRole] : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {i.state === "expired" ? (
                        <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">
                          Süresi doldu
                        </span>
                      ) : (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                          Bekliyor
                        </span>
                      )}
                    </td>
                    <td className="hidden px-3 py-2 text-slate-500 lg:table-cell">
                      {dt(i.sentAt ?? i.createdAt ?? null)}
                    </td>
                    <td className="hidden px-3 py-2 text-slate-500 lg:table-cell">
                      {dt(i.expiresAt ?? null)}
                    </td>
                    <td className="px-3 py-2 text-right lg:whitespace-nowrap">
                      <div className="flex flex-col items-stretch gap-1 lg:flex-row lg:items-center lg:justify-end lg:gap-1">
                        <button
                          type="button"
                          disabled={resend.loading}
                          onClick={async () => {
                            const res = await resend.run({ uid: i.id });
                            if (res)
                              setResent({
                                link: res.inviteLink,
                                expiresAt: res.inviteExpiresAt,
                                email: i.email ?? "",
                              });
                          }}
                          className="inline-flex min-h-[44px] items-center justify-center rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100 disabled:opacity-40 lg:min-h-0 lg:px-2"
                        >
                          Yeniden Gönder
                        </button>
                        <button
                          type="button"
                          disabled={revoke.loading}
                          onClick={() => setRevoking(i)}
                          className="inline-flex min-h-[44px] items-center justify-center rounded border border-red-300 px-3 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40 lg:min-h-0 lg:px-2"
                        >
                          Daveti İptal Et
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ErrorNote error={revoke.error} />
        </section>
      ) : null}

      {inviting ? <InviteModal onClose={() => setInviting(false)} /> : null}

      {/* ── Davet iptali onayı ───────────────────────────────────────────────
          Geri alınamaz bir işlem: iptal edilen davet listeden düşer. Ne olacağı
          ve ne OLMAYACAĞI (hesap silinmez) burada açıkça yazılır — yönetici
          "listeyi temizleyeyim" derken hesap sildiğini sanmasın. */}
      {revoking ? (
        <Modal open onClose={() => setRevoking(null)} title="Daveti İptal Et">
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              <strong>{revoking.email}</strong> adresine gönderilen davet iptal edilecek.
            </p>
            <ul className="list-inside list-disc space-y-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <li>Davet listeden kaldırılır; kayıt denetim izi olarak saklanır.</li>
              <li>
                <strong>Kullanıcı kaydı ve giriş hesabı silinmez.</strong> Hesabı da
                kaldırmak istiyorsanız yukarıdaki listeden <em>Sil</em> düğmesini kullanın.
              </li>
              {revoking.state === "pending" ? (
                <li className="text-amber-900">
                  Bu davetin süresi <strong>henüz dolmadı</strong>. Firebase'in ürettiği
                  şifre bağlantısı sunucudan geri alınamaz; kalan süre boyunca (en fazla
                  1 saat) çalışmaya devam edebilir.
                </li>
              ) : (
                <li>Bu davetin süresi zaten dolmuştu; bağlantı çalışmıyor.</li>
              )}
            </ul>
            <ErrorNote error={revoke.error} />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRevoking(null)}
                className="rounded border border-slate-300 px-4 py-2 text-sm"
              >
                Vazgeç
              </button>
              <button
                type="button"
                disabled={revoke.loading}
                onClick={async () => {
                  const res = await revoke.run({ uid: revoking.id });
                  if (res) {
                    toast.success(res.message);
                    setRevoking(null);
                  }
                }}
                className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {revoke.loading ? "İptal ediliyor…" : "Daveti iptal et"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* ── Öksüz kayıt temizliği onayı ─────────────────────────────────── */}
      {cleaning ? (
        <Modal open onClose={() => setCleaning(null)} title="Öksüz Kaydı Temizle">
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              <strong>{cleaning.fullName || cleaning.email || cleaning.uid}</strong> kaydı
              kapatılacak.
            </p>
            <ul className="list-inside list-disc space-y-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <li>
                Bu kaydın Firebase Auth hesabı <strong>zaten yok</strong>; silinecek bir giriş
                hesabı bulunmuyor.
              </li>
              <li>Kayıt kapatılır ve listelerden düşer.</li>
              <li>
                Firestore dokümanı <strong>silinmez</strong> — geçmiş işlemlerin “kim yaptı”
                bağlantısı ve denetim kaydı kopmamalı.
              </li>
              <li>İlgili “yetki ile kayıt uyuşmuyor” operasyon uyarısı kapatılır.</li>
            </ul>
            <ErrorNote error={cleanOrphan.error} />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCleaning(null)}
                className="rounded border border-slate-300 px-4 py-2 text-sm"
              >
                Vazgeç
              </button>
              <button
                type="button"
                disabled={cleanOrphan.loading}
                onClick={async () => {
                  const res = await cleanOrphan.run({ uid: cleaning.uid });
                  if (res) {
                    toast.success(res.message);
                    // Satır listeden hemen düşsün; tarama her açılışta yenilenir.
                    setOrphans((prev) => prev.filter((o) => o.uid !== cleaning.uid));
                    setCleaning(null);
                  }
                }}
                className="rounded bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {cleanOrphan.loading ? "Temizleniyor…" : "Kaydı temizle"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {deleting ? (
        <Modal open onClose={() => setDeleting(null)} title="Kullanıcıyı Sil">
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              <strong>{deleting.fullName || deleting.email}</strong> hesabı silinecek.
            </p>
            <ul className="list-inside list-disc space-y-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <li>Giriş hesabı silinir; kişi bir daha giriş yapamaz.</li>
              <li>
                <strong>{deleting.email}</strong> adresi serbest kalır — aynı adresle
                yeniden davet edebilirsiniz.
              </li>
              <li>
                Geçmiş işlemleri ve denetim kaydı <strong>silinmez</strong>; kimin ne
                yaptığı izlenebilir kalır.
              </li>
            </ul>
            <ErrorNote error={remove.error} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleting(null)} className="rounded border border-slate-300 px-4 py-2 text-sm">
                Vazgeç
              </button>
              <button
                type="button"
                disabled={remove.loading}
                onClick={async () => {
                  const res = await remove.run({ uid: deleting.id });
                  if (res) {
                    toast.success(res.message);
                    setDeleting(null);
                  }
                }}
                className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {remove.loading ? "Siliniyor…" : "Kalıcı olarak sil"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* Yenilenen davetin bağlantısı — sistem e-posta göndermediği için
          bağlantının kullanıcıya ulaşmasının TEK yolu budur. */}
      {resent ? (
        <Modal open onClose={() => setResent(null)} title="Yeni Davet Bağlantısı">
          <div className="space-y-3">
            <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <strong>Bu bağlantıyı kullanıcıya siz iletin.</strong> Sistem otomatik e-posta göndermez.
            </p>
            <textarea
              readOnly
              rows={3}
              value={resent.link}
              onFocus={(e) => e.currentTarget.select()}
              className={`${INPUT} font-mono text-xs`}
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard
                    .writeText(resent.link)
                    .then(() => toast.success("Bağlantı kopyalandı."))
                    .catch(() => toast.error("Kopyalanamadı — elle seçip kopyalayın."));
                }}
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                Bağlantıyı Kopyala
              </button>
              <button type="button" onClick={() => setResent(null)} className="rounded border border-slate-300 px-4 py-2 text-sm">
                Kapat
              </button>
            </div>
            <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <strong>Yaklaşık 1 saat geçerli</strong> —{" "}
              {new Date(resent.expiresAt).toLocaleString("tr-TR")}'e kadar. Kişiye
              hemen iletin.
            </p>
          </div>
        </Modal>
      ) : null}

      {confirm ? (
        <Modal
          open
          onClose={() => setConfirm(null)}
          title={confirm.next ? "Kullanıcıyı aktifleştir" : "Kullanıcıyı devre dışı bırak"}
        >
          <p className="text-sm text-slate-700">
            <strong>{confirm.user.fullName}</strong> hesabı{" "}
            {confirm.next ? "yeniden aktifleştirilecek" : "devre dışı bırakılacak"}.
            {confirm.next ? null : (
              <> Açık oturumları da sonlandırılır ve giriş yapamaz.</>
            )}
          </p>
          <ErrorNote error={setActive.error} />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 px-4 py-2 text-sm"
              onClick={() => setConfirm(null)}
            >
              Vazgeç
            </button>
            <button
              type="button"
              disabled={setActive.loading}
              className={
                "rounded px-4 py-2 text-sm font-medium text-white disabled:opacity-50 " +
                (confirm.next ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700")
              }
              onClick={() => void applyActive(confirm.user, confirm.next)}
            >
              {setActive.loading ? "Uygulanıyor…" : "Onayla"}
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DAVET — ŞİFRE ALANI YOK
// ═══════════════════════════════════════════════════════════════════════════

function InviteModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const create = useCallableAction<
    { email: string; fullName: string; role: UserRole },
    { uid: string; fullName: string; inviteLink: string; inviteExpiresAt: number }
  >("createErpUser");

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<UserRole>("production");
  const [localError, setLocalError] = useState<string | null>(null);
  const [invite, setInvite] = useState<{ link: string; expiresAt: number; email: string } | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit() {
    setLocalError(null);
    const mail = email.trim().toLowerCase();
    if (!mail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      setLocalError("Geçerli bir e-posta adresi girin.");
      return;
    }
    if (!fullName.trim()) {
      setLocalError("Ad soyad zorunludur.");
      return;
    }
    const res = await create.run({ email: mail, fullName: fullName.trim(), role });
    if (res) {
      // ★ E-POSTA GÖNDERİLMİYOR — bağlantı ÜRETİLİYOR.
      // Burada "davet gönderildi" yazıp pencereyi kapatıyorduk; oysa Firebase'in
      // `generatePasswordResetLink` çağrısı yalnızca bağlantıyı üretir, hiçbir
      // e-posta atmaz. Sistemde kurulu bir e-posta sağlayıcısı da yok. Yani
      // kullanıcıya HİÇBİR ŞEY ulaşmıyordu ve ekran başarı diyordu.
      // Bağlantı artık ekranda gösterilir ve iş sahibi kendisi iletir.
      setInvite({ link: res.inviteLink, expiresAt: res.inviteExpiresAt, email: mail });
    }
  }

  // Davet üretildikten sonra form yerine BAĞLANTI gösterilir.
  if (invite) {
    return (
      <Modal open onClose={onClose} title="Davet Bağlantısı Hazır">
        <div className="space-y-3">
          <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong>Bu bağlantıyı kullanıcıya siz iletin.</strong> Sistem otomatik e-posta
            göndermez. Bağlantıya tıklayan kişi kendi şifresini belirler ve giriş yapar.
          </p>

          <Field label={`${invite.email} için davet bağlantısı`}>
            <textarea
              readOnly
              rows={3}
              value={invite.link}
              onFocus={(e) => e.currentTarget.select()}
              className={`${INPUT} font-mono text-xs`}
            />
          </Field>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                navigator.clipboard
                  .writeText(invite.link)
                  .then(() => { setCopied(true); toast.success("Bağlantı kopyalandı."); })
                  .catch(() => toast.error("Kopyalanamadı — bağlantıyı elle seçip kopyalayın."));
              }}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              {copied ? "Kopyalandı ✓" : "Bağlantıyı Kopyala"}
            </button>
            <a
              href={`mailto:${encodeURIComponent(invite.email)}?subject=${encodeURIComponent(
                "Balder ERP davetiniz",
              )}&body=${encodeURIComponent(
                `Merhaba,

Balder ERP hesabınız açıldı. Aşağıdaki bağlantıdan kendi şifrenizi belirleyip giriş yapabilirsiniz:

${invite.link}

Bağlantı sınırlı süre geçerlidir.`,
              )}`}
              className="rounded border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100"
            >
              E-posta ile aç
            </a>
          </div>

          <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <strong>Bağlantı yaklaşık 1 saat geçerlidir</strong> (Firebase'in sınırı,
            değiştirilemez) — {new Date(invite.expiresAt).toLocaleString("tr-TR")}'e kadar.
            Kişi bu süre içinde şifresini belirlemezse bağlantı ölür ve hesabı
            <strong> şifresiz kalır</strong>; giriş denediğinde reddedilir. Bu durumda
            kullanıcı listesinden <strong>“Daveti Yenile”</strong> ile yeni bağlantı
            üretin. Bağlantıyı kişiye <strong>şimdi</strong> iletin.
          </p>
        </div>

        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onClose} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">
            Kapat
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="Kullanıcı Davet Et">
      <div className="grid grid-cols-1 gap-3">
        <Field label="Ad Soyad">
          <input className={INPUT} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <Field label="E-posta" hint="Davet bağlantısı bu adrese gönderilir.">
          <input
            className={INPUT}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Rol">
          <select
            className={INPUT}
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
          >
            {(Object.keys(ROLE_TR) as UserRole[]).map((r) => (
              <option key={r} value={r}>
                {ROLE_TR[r]}
              </option>
            ))}
          </select>
        </Field>
        <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {ROLE_HINT[role]}
        </p>
        <p className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          Şifre burada belirlenmez. Kullanıcı davet bağlantısıyla kendi şifresini oluşturur.
        </p>
      </div>

      <ErrorNote error={localError ?? create.error} />

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded border border-slate-300 px-4 py-2 text-sm">
          Vazgeç
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={create.loading}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {create.loading ? "Gönderiliyor…" : "Daveti Gönder"}
        </button>
      </div>
    </Modal>
  );
}
